import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import * as DTO from "../../../interfaces";
import { useSocialCoachSession, URLS, userFullName, printOnlyDate, useAccountSettings } from "../../../utils";
import { useEffect, useMemo } from "react";
import { AssistantServices } from "../../../services";
import { Combobox, Button, InputGroup, Input } from "@/components/ui";
import { Loader, Message, Table } from "semantic-ui-react";
import { Search } from "lucide-react";
import { WebComponents } from "../../../interfaces";

type Props = RouteComponentProps;

const AssistantsFC: React.FC<Props> = ({ history }) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.ASSISTANTS);

  const [searchTerm, setSearchTerm] = React.useState("");
  const statusTypes = ["Active", "Cancelled", "Pending"] as const;
  type StatusType = (typeof statusTypes)[number];
  const [status, setStatus] = React.useState<StatusType>("Active");
  const [activeAssistants, setActiveAssistants] = React.useState<DTO.Assistant[] | undefined>(undefined);
  const [cancelledAssistants, setCancelledAssistants] = React.useState<DTO.Assistant[]>([]);
  const [pendingAssistants, setPendingAssistants] = React.useState<DTO.Assistant[]>([]);

  const [loadingActive, setLoadingActive] = React.useState<boolean>(true);
  const [loadingPending, setLoadingPending] = React.useState<boolean>(true);
  const [loadingCancelled, setLoadingCancelled] = React.useState<boolean>(true);

  useEffect(() => {
    loadAssistants().catch(console.error);
    loadCancelled().catch(console.error);
    loadPending().catch(console.error);
  }, [coachId]);

  const loadAssistants = async () => {
    if (coachId) {
      setLoadingActive(true);
      const res = await AssistantServices.getByCoachId(coachId.toString());
      setActiveAssistants(res);
      setLoadingActive(false);
    }
  };

  const loadCancelled = async () => {
    if (coachId) {
      setLoadingCancelled(true);
      const res = await AssistantServices.getInactive(coachId.toString());
      setCancelledAssistants(res);
      setLoadingCancelled(false);
    }
  };

  const loadPending = async () => {
    if (coachId) {
      setLoadingPending(true);
      const res = await AssistantServices.getPending(coachId.toString());
      setPendingAssistants(res);
      setLoadingPending(false);
    }
  };

  const assistants = useMemo(() => {
    switch (status) {
      case "Active":
        return activeAssistants ?? [];
      case "Cancelled":
        return cancelledAssistants ?? [];
      case "Pending":
        return pendingAssistants ?? [];
      default:
        return [];
    }
  }, [status, activeAssistants, cancelledAssistants, pendingAssistants]);

  const filteredAssistants = useMemo(() => {
    if (!searchTerm) return assistants;
    else
      return (
        assistants.filter(a => {
          const values = [
            a.account.profile.firstName,
            a.account.profile.lastName,
            a.account.user.email,
            ...(a.regionNames || []),
            ...(a.branchNames || []),
          ];
          return values.some(v => v?.toLowerCase()?.includes(searchTerm.toLowerCase()));
        }) || []
      );
  }, [searchTerm, assistants]);

  const loading = useMemo(() => {
    switch (status) {
      case "Active":
        return loadingActive;
      case "Cancelled":
        return loadingCancelled;
      case "Pending":
        return loadingPending;
    }
  }, [status, loadingActive, loadingCancelled, loadingPending]);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <InputGroup
            className="w-50"
            control={controlProps => (
              <Input
                {...controlProps}
                placeholder="Search Assistant..."
                onChange={e => setSearchTerm(e.target.value)}
              />
            )}
            append={<Search />}
          />

          <Combobox
            className="w-50"
            items={statusTypes}
            value={status}
            onValueChange={data => setStatus(data ?? "Active")}
          />
        </div>
        {withWritePermission && (
          <Button onClick={() => history.push(URLS.coach.newAssistant)} children="Add Assistant" />
        )}
      </div>

      {assistants.length > 0 ? (
        <Table striped compact="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Name & Email</Table.HeaderCell>
              <Table.HeaderCell>Last Login</Table.HeaderCell>
              <Table.HeaderCell>Regions</Table.HeaderCell>
              <Table.HeaderCell>Branches</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {filteredAssistants.map(assistant => {
              const goToAssistantDetail = () => {
                const assistantUrl = URLS.coach.editAssistant.replace(":userId", `${assistant?.account?.user?.userId}`);
                history.push(assistantUrl);
              };
              return (
                <Table.Row
                  onClick={goToAssistantDetail}
                  className="cursor-pointer"
                  key={`coach_element_${assistant?.account?.user?.userId}`}
                >
                  <Table.Cell className="text-sm text-foreground">
                    {assistant?.account && (
                      <div className="font-semibold">{userFullName(assistant.account.profile)}</div>
                    )}
                    <div>{assistant?.account?.user?.email?.toLocaleLowerCase()}</div>
                  </Table.Cell>
                  <Table.Cell>
                    {assistant?.account?.user?.latestLoginDate &&
                      printOnlyDate(assistant?.account?.user?.latestLoginDate!)}
                  </Table.Cell>
                  <Table.Cell>{assistant?.regionNames?.join(", ")}</Table.Cell>
                  <Table.Cell>{assistant?.branchNames?.join(", ")}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : loading ? (
        <Loader active size="large" />
      ) : (
        <Message icon="search" header="No results" content="There are no assistants in this section" size="large" />
      )}
    </div>
  );
};

export const Assistants = withRouter(AssistantsFC);
